import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["block", "component", "className", "disabled", "loading", "primary", "tertiary", "children", "dangerouslyForceFocusStyling", "dangerouslyForceActiveStyling", "dangerouslyForceHoverStyling", "short"];
import React, { Fragment } from 'react';
import classNames from 'classnames';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import MotionRotateScaleFade from './motion-rotate-scale-fade';
import styles from "./button.module.css";
var inferComponent = function inferComponent(_ref) {
  var href = _ref.href;
  return href ? 'a' : 'button';
};
var preventDefaultAndStopPropagation = function preventDefaultAndStopPropagation(e) {
  e.preventDefault();
  e.stopPropagation();
};
var getDisabledProps = function getDisabledProps(Component, disabled) {
  if (!disabled) {
    return {};
  }
  if (Component === 'a') {
    return {
      tabIndex: -1,
      'aria-disabled': true,
      onMouseDown: preventDefaultAndStopPropagation,
      onClick: preventDefaultAndStopPropagation
    };
  }
  return {
    disabled: true
  };
};
var getTypeProps = function getTypeProps(Component) {
  // Should use type=button when using a <button> component, but not needed
  // when using <a>. This can be overridden by passing a `type` prop.
  if (Component === 'button') {
    return {
      type: 'button'
    };
  }
  return {};
};
var Button = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var _classNames, _classNames2;
  var _ref2$block = _ref2.block,
    block = _ref2$block === void 0 ? false : _ref2$block,
    component = _ref2.component,
    className = _ref2.className,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    _ref2$loading = _ref2.loading,
    loading = _ref2$loading === void 0 ? false : _ref2$loading,
    _ref2$primary = _ref2.primary,
    primary = _ref2$primary === void 0 ? false : _ref2$primary,
    _ref2$tertiary = _ref2.tertiary,
    tertiary = _ref2$tertiary === void 0 ? false : _ref2$tertiary,
    children = _ref2.children,
    _ref2$dangerouslyForc = _ref2.dangerouslyForceFocusStyling,
    dangerouslyForceFocusStyling = _ref2$dangerouslyForc === void 0 ? false : _ref2$dangerouslyForc,
    _ref2$dangerouslyForc2 = _ref2.dangerouslyForceActiveStyling,
    dangerouslyForceActiveStyling = _ref2$dangerouslyForc2 === void 0 ? false : _ref2$dangerouslyForc2,
    _ref2$dangerouslyForc3 = _ref2.dangerouslyForceHoverStyling,
    dangerouslyForceHoverStyling = _ref2$dangerouslyForc3 === void 0 ? false : _ref2$dangerouslyForc3,
    _ref2$short = _ref2.short,
    short = _ref2$short === void 0 ? false : _ref2$short,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var Component = component || inferComponent(otherProps);
  var buttonWrapperClassNames = classNames(className, styles.buttonWrapper, Component === 'a' && styles['link-button'], (_classNames = {}, _defineProperty(_classNames, styles.shortButtonWrapper, short), _defineProperty(_classNames, styles.block, block), _defineProperty(_classNames, styles.disabled, disabled), _classNames));
  var buttonClassNames = classNames(styles.button, (_classNames2 = {}, _defineProperty(_classNames2, styles.loading, loading), _defineProperty(_classNames2, styles.primary, !disabled && primary), _defineProperty(_classNames2, styles.secondary, !disabled && !primary && !tertiary), _defineProperty(_classNames2, styles.tertiary, !disabled && !primary && tertiary), _defineProperty(_classNames2, styles['primary-disabled'], disabled && primary), _defineProperty(_classNames2, styles['secondary-disabled'], disabled && !primary && !tertiary), _defineProperty(_classNames2, styles['tertiary-disabled'], disabled && !primary && tertiary), _defineProperty(_classNames2, styles['force-focus'], dangerouslyForceFocusStyling), _defineProperty(_classNames2, styles['force-hover'], dangerouslyForceHoverStyling), _defineProperty(_classNames2, styles['force-active'], dangerouslyForceActiveStyling), _classNames2));
  var buttonContent = /*#__PURE__*/React.createElement(Fragment, null, loading && /*#__PURE__*/React.createElement("div", {
    className: styles['loading-container']
  }, /*#__PURE__*/React.createElement(MotionRotateScaleFade, {
    show: loading
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    size: short ? 16 : 24,
    color: primary ? 'green-light' : 'green'
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, children));
  return /*#__PURE__*/React.createElement(Component
  // bind a noop ontouchstart handler
  // so that ios will style :active states
  , _extends({
    onTouchStart: function onTouchStart() {},
    className: classNames(buttonWrapperClassNames, !short && buttonClassNames),
    ref: ref
  }, getTypeProps(Component), {
    primary: primary,
    tertiary: tertiary
  }, otherProps, getDisabledProps(Component, disabled)), short ? /*#__PURE__*/React.createElement("div", {
    className: styles.shortButtonPadding
  }, /*#__PURE__*/React.createElement("div", {
    className: buttonClassNames
  }, buttonContent)) : buttonContent);
});
Button.displayName = 'Button';
export default Button;